// Default indicator configurations with editable code
// Each indicator's `code` is a JavaScript function body that receives:
//   issues: JiraIssue[]           - all fetched issues with changelogs
//   targetDevs: string[]          - lowercase developer display names
//   devMap: Map<string, string>   - dev name → accountId
//   helpers: object               - utility functions (isTestingStatus, isDoneStatus, etc.)
//   devInfo: Map<string, object>  - issue ID → { commits, pullRequests } (for commit-checking indicators)
//
// Must return: { id, name, description, results, overallValue, overallLabel, severity }

import type { IndicatorConfig } from "./types";

export type { IndicatorConfig };

export const DEFAULT_INDICATORS: IndicatorConfig[] = [
  // ==================== LAG INDICATORS (1-9) ====================
  {
    id: 1,
    name: "% Ready for Testing → DEV Rework",
    description: "Percentage of cards that moved from testing back to DEV Rework",
    type: "lag",
    needsDevInfo: false,
    scope: "per-user",
    source: "jira",
    code: `const devResults = [];

for (const dev of targetDevs) {
  const devIssues = issues.filter(
    (i) => i.fields.assignee?.displayName.toLowerCase() === dev
  );

  let enteredTesting = 0;
  let sentToDevRework = 0;
  const reworkKeys = [];

  for (const issue of devIssues) {
    const histories = issue.changelog?.histories || [];
    let wasInTesting = false;

    for (const history of histories) {
      for (const item of history.items) {
        if (item.field === "status") {
          const toStatus = item.toString || "";
          const fromStatus = item.fromString || "";

          if (helpers.isTestingStatus(toStatus)) {
            enteredTesting++;
            wasInTesting = true;
          }

          if (
            wasInTesting &&
            helpers.isTestingStatus(fromStatus) &&
            (helpers.isDevReworkStatus(toStatus) || helpers.isInProgressStatus(toStatus))
          ) {
            sentToDevRework++;
            reworkKeys.push(issue.key);
            wasInTesting = false;
          }
        }
      }
    }
  }

  const pct = enteredTesting > 0 ? Math.round((sentToDevRework / enteredTesting) * 100) : 0;
  devResults.push({
    developerName: dev,
    developerAccountId: devMap.get(dev) || "",
    value: pct,
    label: pct + "%",
    severity: pct <= 10 ? "good" : pct <= 25 ? "warning" : "critical",
    details: sentToDevRework + "/" + enteredTesting + " cards sent to DEV Rework",
    issueKeys: reworkKeys,
  });
}

const overall = devResults.length > 0 ? Math.round(devResults.reduce((s, r) => s + r.value, 0) / devResults.length) : 0;
return {
  id: 1,
  name: "% Ready for Testing → DEV Rework",
  description: "Percentage of cards that moved from testing (QA Testing, Release Testing) back to DEV Rework",
  results: devResults,
  overallValue: overall,
  overallLabel: overall + "%",
  severity: overall <= 10 ? "good" : overall <= 25 ? "warning" : "critical",
};`,
  },
  {
    id: 2,
    name: "% Ready for Testing → Release Candidate",
    description: "Percentage of cards that passed testing and moved to Release Candidate",
    type: "lag",
    needsDevInfo: false,
    scope: "per-user",
    source: "jira",
    code: `const devResults = [];

for (const dev of targetDevs) {
  const devIssues = issues.filter(
    (i) => i.fields.assignee?.displayName.toLowerCase() === dev
  );

  let enteredTesting = 0;
  let passedToRC = 0;

  for (const issue of devIssues) {
    const histories = issue.changelog?.histories || [];
    let wasInTesting = false;

    for (const history of histories) {
      for (const item of history.items) {
        if (item.field === "status") {
          const toStatus = item.toString || "";
          const fromStatus = item.fromString || "";

          if (helpers.isTestingStatus(toStatus)) {
            enteredTesting++;
            wasInTesting = true;
          }

          if (
            wasInTesting &&
            helpers.isTestingStatus(fromStatus) &&
            (helpers.isReleaseCandidateStatus(toStatus) || helpers.statusMatches(toStatus, helpers.STATUS.READY) || helpers.isDoneStatus(toStatus))
          ) {
            passedToRC++;
            wasInTesting = false;
          }
        }
      }
    }
  }

  const pct = enteredTesting > 0 ? Math.round((passedToRC / enteredTesting) * 100) : 0;
  devResults.push({
    developerName: dev,
    developerAccountId: devMap.get(dev) || "",
    value: pct,
    label: pct + "%",
    severity: pct >= 80 ? "good" : pct >= 60 ? "warning" : "critical",
    details: passedToRC + "/" + enteredTesting + " cards passed to Release Candidate",
  });
}

const overall = devResults.length > 0 ? Math.round(devResults.reduce((s, r) => s + r.value, 0) / devResults.length) : 0;
return {
  id: 2,
  name: "% Ready for Testing → Release Candidate",
  description: "Percentage of cards that passed testing and moved to Release Candidate / Ready for Release",
  results: devResults,
  overallValue: overall,
  overallLabel: overall + "%",
  severity: overall >= 80 ? "good" : overall >= 60 ? "warning" : "critical",
};`,
  },
  {
    id: 3,
    name: "Bugs within 30 days of completion",
    description: "Number of bugs raised against a component within 30 days of cards being marked complete",
    type: "lag",
    needsDevInfo: false,
    scope: "per-user",
    source: "jira",
    code: `const devResults = [];
const bugs = issues.filter((i) => i.fields.issuetype.name.toLowerCase() === "bug");

const completedByComponent = new Map();
for (const issue of issues) {
  if (issue.fields.issuetype.name.toLowerCase() === "bug") continue;
  if (!helpers.isDoneStatus(issue.fields.status.name)) continue;

  const completedAt = issue.fields.resolutiondate
    ? new Date(issue.fields.resolutiondate)
    : new Date(issue.fields.updated);

  const assignee = issue.fields.assignee?.displayName.toLowerCase() || "";
  const components = issue.fields.components || [];

  for (const comp of components) {
    if (!completedByComponent.has(comp.name)) {
      completedByComponent.set(comp.name, []);
    }
    completedByComponent.get(comp.name).push({ completedAt, assignee, key: issue.key });
  }
}

for (const dev of targetDevs) {
  let bugCount = 0;
  const bugKeys = [];

  for (const bug of bugs) {
    const bugCreated = new Date(bug.fields.created);
    const bugComponents = bug.fields.components || [];

    for (const comp of bugComponents) {
      const completed = completedByComponent.get(comp.name) || [];
      const devCompleted = completed.filter((c) => c.assignee === dev);

      for (const c of devCompleted) {
        const daysDiff = (bugCreated.getTime() - c.completedAt.getTime()) / (1000 * 60 * 60 * 24);
        if (daysDiff >= 0 && daysDiff <= 30) {
          bugCount++;
          bugKeys.push(bug.key);
          break;
        }
      }
    }
  }

  devResults.push({
    developerName: dev,
    developerAccountId: devMap.get(dev) || "",
    value: bugCount,
    label: "" + bugCount,
    severity: bugCount === 0 ? "good" : bugCount <= 3 ? "warning" : "critical",
    details: bugCount + " bug(s) raised within 30 days of component completion",
    issueKeys: bugKeys,
  });
}

const overall = devResults.reduce((s, r) => s + r.value, 0);
return {
  id: 3,
  name: "Bugs within 30 days of completion",
  description: "Number of bugs raised against a component within 30 days of cards being marked complete",
  results: devResults,
  overallValue: overall,
  overallLabel: "" + overall,
  severity: overall === 0 ? "good" : overall <= 5 ? "warning" : "critical",
};`,
  },
  {
    id: 4,
    name: "Estimate overrun rate",
    description: "Percentage of completed cards that exceeded their estimate by more than 100%",
    type: "lag",
    needsDevInfo: false,
    scope: "per-user",
    source: "jira",
    code: `const devResults = [];

for (const dev of targetDevs) {
  const devIssues = issues.filter(
    (i) =>
      i.fields.assignee?.displayName.toLowerCase() === dev &&
      helpers.isDoneStatus(i.fields.status.name)
  );

  let totalCompleted = 0;
  let overrunCount = 0;
  const overrunKeys = [];

  for (const issue of devIssues) {
    const estimate = issue.fields.timeoriginalestimate;
    const spent = issue.fields.timespent;

    if (estimate && estimate > 0) {
      totalCompleted++;
      if (spent && spent > estimate * 2) {
        overrunCount++;
        overrunKeys.push(issue.key);
      }
    }
  }

  const pct = totalCompleted > 0 ? Math.round((overrunCount / totalCompleted) * 100) : 0;
  devResults.push({
    developerName: dev,
    developerAccountId: devMap.get(dev) || "",
    value: pct,
    label: pct + "%",
    severity: pct <= 10 ? "good" : pct <= 25 ? "warning" : "critical",
    details: overrunCount + "/" + totalCompleted + " cards exceeded estimate by >100%",
    issueKeys: overrunKeys,
  });
}

const overall = devResults.length > 0 ? Math.round(devResults.reduce((s, r) => s + r.value, 0) / devResults.length) : 0;
return {
  id: 4,
  name: "Estimate overrun rate",
  description: "Percentage of completed cards that exceeded their estimate by more than 100%",
  results: devResults,
  overallValue: overall,
  overallLabel: overall + "%",
  severity: overall <= 10 ? "good" : overall <= 25 ? "warning" : "critical",
};`,
  },
  {
    id: 5,
    name: "Cards completed without commit",
    description: "Number of completed cards that have no linked commits",
    type: "lag",
    needsDevInfo: true,
    scope: "per-user",
    source: "both",
    code: `const devResults = [];

for (const dev of targetDevs) {
  const devIssues = issues.filter(
    (i) =>
      i.fields.assignee?.displayName.toLowerCase() === dev &&
      helpers.isDoneStatus(i.fields.status.name) &&
      i.fields.issuetype.name.toLowerCase() !== "bug"
  );

  let noCommitCount = 0;
  const noCommitKeys = [];

  for (const issue of devIssues) {
    const info = devInfo.get(issue.id);
    if (!info || info.commits === 0) {
      noCommitCount++;
      noCommitKeys.push(issue.key);
    }
  }

  devResults.push({
    developerName: dev,
    developerAccountId: devMap.get(dev) || "",
    value: noCommitCount,
    label: "" + noCommitCount,
    severity: noCommitCount === 0 ? "good" : noCommitCount <= 2 ? "warning" : "critical",
    details: noCommitCount + " completed card(s) without linked commits",
    issueKeys: noCommitKeys,
  });
}

const overall = devResults.reduce((s, r) => s + r.value, 0);
return {
  id: 5,
  name: "Cards completed without commit",
  description: "Number of completed cards that have no linked commits",
  results: devResults,
  overallValue: overall,
  overallLabel: "" + overall,
  severity: overall === 0 ? "good" : overall <= 3 ? "warning" : "critical",
};`,
  },
  {
    id: 6,
    name: "Hotfix bugs without commit",
    description: "Number of hotfix/critical bugs marked Complete without an attached commit",
    type: "lag",
    needsDevInfo: true,
    scope: "per-user",
    source: "both",
    code: `const devResults = [];

for (const dev of targetDevs) {
  const hotfixBugs = issues.filter(
    (i) =>
      i.fields.assignee?.displayName.toLowerCase() === dev &&
      helpers.isDoneStatus(i.fields.status.name) &&
      i.fields.issuetype.name.toLowerCase() === "bug" &&
      (i.fields.priority?.name.toLowerCase() === "highest" ||
        i.fields.priority?.name.toLowerCase() === "critical" ||
        i.fields.labels?.some((l) => l.toLowerCase().includes("hotfix")) ||
        i.fields.labels?.some((l) => l.toLowerCase().includes("production")) ||
        helpers.isProductionHotfix(i.fields.status.name))
  );

  let noCommitCount = 0;
  const noCommitKeys = [];

  for (const issue of hotfixBugs) {
    const info = devInfo.get(issue.id);
    if (!info || info.commits === 0) {
      noCommitCount++;
      noCommitKeys.push(issue.key);
    }
  }

  devResults.push({
    developerName: dev,
    developerAccountId: devMap.get(dev) || "",
    value: noCommitCount,
    label: "" + noCommitCount,
    severity: noCommitCount === 0 ? "good" : noCommitCount <= 1 ? "warning" : "critical",
    details: noCommitCount + " hotfix bug(s) completed without linked commits",
    issueKeys: noCommitKeys,
  });
}

const overall = devResults.reduce((s, r) => s + r.value, 0);
return {
  id: 6,
  name: "Hotfix bugs without commit",
  description: "Number of hotfix/critical bugs marked Complete without an attached commit",
  results: devResults,
  overallValue: overall,
  overallLabel: "" + overall,
  severity: overall === 0 ? "good" : overall <= 2 ? "warning" : "critical",
};`,
  },
  {
    id: 7,
    name: "Average cycle time",
    description: "Mean time from In Progress to Completed/Live in Production per developer (in days)",
    type: "lag",
    needsDevInfo: false,
    scope: "per-user",
    source: "jira",
    code: `const devResults = [];

for (const dev of targetDevs) {
  const devIssues = issues.filter(
    (i) =>
      i.fields.assignee?.displayName.toLowerCase() === dev &&
      helpers.isDoneStatus(i.fields.status.name)
  );

  const cycleTimes = [];

  for (const issue of devIssues) {
    const histories = issue.changelog?.histories || [];
    let inProgressDate = null;
    let doneDate = null;

    for (const history of histories) {
      for (const item of history.items) {
        if (item.field === "status") {
          const toStatus = item.toString || "";
          if (!inProgressDate && helpers.isInProgressStatus(toStatus)) {
            inProgressDate = new Date(history.created);
          }
          if (helpers.isDoneStatus(toStatus)) {
            doneDate = new Date(history.created);
          }
        }
      }
    }

    if (inProgressDate && doneDate) {
      const days = (doneDate.getTime() - inProgressDate.getTime()) / (1000 * 60 * 60 * 24);
      if (days > 0) cycleTimes.push(days);
    }
  }

  const avg = cycleTimes.length > 0 ? cycleTimes.reduce((a, b) => a + b, 0) / cycleTimes.length : 0;
  const rounded = Math.round(avg * 10) / 10;

  devResults.push({
    developerName: dev,
    developerAccountId: devMap.get(dev) || "",
    value: rounded,
    label: rounded + " days",
    severity: rounded <= 5 ? "good" : rounded <= 14 ? "warning" : "critical",
    details: "Average across " + cycleTimes.length + " completed issues",
  });
}

const overall = devResults.length > 0
  ? Math.round((devResults.reduce((s, r) => s + r.value, 0) / devResults.length) * 10) / 10
  : 0;

return {
  id: 7,
  name: "Average cycle time",
  description: "Mean time from In Progress to Completed/Live in Production per developer (in days)",
  results: devResults,
  overallValue: overall,
  overallLabel: overall + " days",
  severity: overall <= 5 ? "good" : overall <= 14 ? "warning" : "critical",
};`,
  },
  {
    id: 8,
    name: "Sprint scope creep",
    description: "Percentage of cards added to sprints after the sprint has started",
    type: "lag",
    needsDevInfo: false,
    scope: "per-user",
    source: "jira",
    code: `const devResults = [];

const sprintIssues = new Map();
for (const issue of issues) {
  const sprint = issue.fields.sprint;
  if (sprint?.id) {
    if (!sprintIssues.has(sprint.id)) sprintIssues.set(sprint.id, []);
    sprintIssues.get(sprint.id).push(issue);
  }
}

for (const dev of targetDevs) {
  let totalOriginal = 0;
  let totalAdded = 0;

  for (const [, sprintIssueList] of sprintIssues) {
    const devSprintIssues = sprintIssueList.filter(
      (i) => i.fields.assignee?.displayName.toLowerCase() === dev
    );

    for (const issue of devSprintIssues) {
      const sprint = issue.fields.sprint;
      if (!sprint?.startDate) continue;

      const sprintStart = new Date(sprint.startDate);
      const histories = issue.changelog?.histories || [];

      let addedToSprintDate = null;
      for (const history of histories) {
        for (const item of history.items) {
          if (item.field === "Sprint" && item.toString?.includes(sprint.name)) {
            addedToSprintDate = new Date(history.created);
          }
        }
      }

      if (addedToSprintDate && addedToSprintDate > sprintStart) {
        totalAdded++;
      } else {
        totalOriginal++;
      }
    }
  }

  const total = totalOriginal + totalAdded;
  const pct = total > 0 ? Math.round((totalAdded / total) * 100) : 0;

  devResults.push({
    developerName: dev,
    developerAccountId: devMap.get(dev) || "",
    value: pct,
    label: pct + "%",
    severity: pct <= 15 ? "good" : pct <= 30 ? "warning" : "critical",
    details: totalAdded + " cards added after sprint start (of " + total + " total)",
  });
}

const overall = devResults.length > 0 ? Math.round(devResults.reduce((s, r) => s + r.value, 0) / devResults.length) : 0;
return {
  id: 8,
  name: "Sprint scope creep",
  description: "Percentage of cards added to sprints after the sprint has started",
  results: devResults,
  overallValue: overall,
  overallLabel: overall + "%",
  severity: overall <= 15 ? "good" : overall <= 30 ? "warning" : "critical",
};`,
  },
  {
    id: 9,
    name: "Reopened issues rate",
    description: "Percentage of issues moved back to In Progress/DEV Rework after being in testing or done",
    type: "lag",
    needsDevInfo: false,
    scope: "per-user",
    source: "jira",
    code: `const devResults = [];

for (const dev of targetDevs) {
  const devIssues = issues.filter(
    (i) => i.fields.assignee?.displayName.toLowerCase() === dev
  );

  let totalIssues = devIssues.length;
  let reopenedCount = 0;
  const reopenedKeys = [];

  for (const issue of devIssues) {
    const histories = issue.changelog?.histories || [];
    let wasResolved = false;

    for (const history of histories) {
      for (const item of history.items) {
        if (item.field === "status") {
          const toStatus = item.toString || "";
          const fromStatus = item.fromString || "";

          if (helpers.isDoneStatus(toStatus) || helpers.isTestingStatus(toStatus) || helpers.isReleaseCandidateStatus(toStatus)) {
            wasResolved = true;
          }

          if (
            wasResolved &&
            (helpers.isDoneStatus(fromStatus) || helpers.isTestingStatus(fromStatus) || helpers.isReleaseCandidateStatus(fromStatus)) &&
            (helpers.isInProgressStatus(toStatus) || helpers.isDevReworkStatus(toStatus) || helpers.statusMatches(toStatus, helpers.STATUS.TODO))
          ) {
            reopenedCount++;
            reopenedKeys.push(issue.key);
            wasResolved = false;
            break;
          }
        }
      }
    }
  }

  const pct = totalIssues > 0 ? Math.round((reopenedCount / totalIssues) * 100) : 0;
  devResults.push({
    developerName: dev,
    developerAccountId: devMap.get(dev) || "",
    value: pct,
    label: pct + "%",
    severity: pct <= 5 ? "good" : pct <= 15 ? "warning" : "critical",
    details: reopenedCount + "/" + totalIssues + " issues reopened",
    issueKeys: reopenedKeys,
  });
}

const overall = devResults.length > 0 ? Math.round(devResults.reduce((s, r) => s + r.value, 0) / devResults.length) : 0;
return {
  id: 9,
  name: "Reopened issues rate",
  description: "Percentage of issues moved back to In Progress/DEV Rework after being in testing or done",
  results: devResults,
  overallValue: overall,
  overallLabel: overall + "%",
  severity: overall <= 5 ? "good" : overall <= 15 ? "warning" : "critical",
};`,
  },

  // ==================== LEAD INDICATORS (10-18) ====================
  {
    id: 10,
    name: "Issues completed",
    description: "Issues that reached Done — attributed to whoever moved them out of In Progress",
    type: "lead",
    needsDevInfo: false,
    scope: "per-user",
    source: "jira",
    code: `const devCounts = new Map();
for (const dev of targetDevs) devCounts.set(dev, { count: 0, keys: [] });

for (const issue of issues) {
  if (!helpers.isDoneStatus(issue.fields.status.name)) continue;

  const histories = issue.changelog?.histories || [];
  const sorted = [...histories].sort(
    (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime()
  );

  let devWhoCompleted = null;

  for (const history of sorted) {
    for (const item of history.items) {
      if (item.field === "status") {
        const fromStatus = item.fromString || "";
        const toStatus = item.toString || "";

        if (
          helpers.isInProgressStatus(fromStatus) &&
          (helpers.isTestingStatus(toStatus) || helpers.isReleaseCandidateStatus(toStatus) ||
           helpers.isDoneStatus(toStatus) || helpers.statusMatches(toStatus, helpers.STATUS.READY))
        ) {
          const author = history.author?.displayName?.toLowerCase();
          if (author) devWhoCompleted = author;
        }
      }
    }
  }

  if (!devWhoCompleted) {
    for (const history of sorted) {
      for (const item of history.items) {
        if (item.field === "status" && helpers.isDoneStatus(item.toString || "")) {
          const author = history.author?.displayName?.toLowerCase();
          if (author) devWhoCompleted = author;
          break;
        }
      }
      if (devWhoCompleted) break;
    }
  }

  if (devWhoCompleted && devCounts.has(devWhoCompleted)) {
    const entry = devCounts.get(devWhoCompleted);
    entry.count++;
    entry.keys.push(issue.key);
  }
}

const devResults = [];
for (const dev of targetDevs) {
  const entry = devCounts.get(dev);
  devResults.push({
    developerName: dev, developerAccountId: devMap.get(dev) || "",
    value: entry.count, label: "" + entry.count,
    severity: entry.count >= 15 ? "good" : entry.count >= 5 ? "warning" : "critical",
    details: entry.count + " issues completed (moved out of In Progress)",
    issueKeys: entry.keys,
  });
}
const overall = devResults.reduce((s, r) => s + r.value, 0);
return {
  id: 10, name: "Issues completed",
  description: "Issues that reached Done — attributed to whoever moved them out of In Progress",
  results: devResults, overallValue: overall, overallLabel: "" + overall,
  severity: overall >= 30 ? "good" : overall >= 10 ? "warning" : "critical",
};`,
  },
  {
    id: 11,
    name: "First-pass rate",
    description: "Percentage of cards that passed testing without any rework",
    type: "lead",
    needsDevInfo: false,
    scope: "per-user",
    source: "jira",
    code: `const devResults = [];
for (const dev of targetDevs) {
  const devIssues = issues.filter(
    (i) => i.fields.assignee?.displayName.toLowerCase() === dev
  );
  let enteredTesting = 0;
  let passedFirstTime = 0;
  for (const issue of devIssues) {
    const histories = issue.changelog?.histories || [];
    let wasInTesting = false;
    let hadRework = false;
    let passed = false;
    for (const history of histories) {
      for (const item of history.items) {
        if (item.field === "status") {
          const toStatus = item.toString || "";
          const fromStatus = item.fromString || "";
          if (helpers.isTestingStatus(toStatus)) { wasInTesting = true; enteredTesting++; }
          if (wasInTesting && helpers.isTestingStatus(fromStatus) && (helpers.isDevReworkStatus(toStatus) || helpers.isInProgressStatus(toStatus))) { hadRework = true; }
          if (wasInTesting && (helpers.isReleaseCandidateStatus(toStatus) || helpers.isDoneStatus(toStatus))) { passed = true; }
        }
      }
    }
    if (passed && !hadRework) passedFirstTime++;
  }
  const pct = enteredTesting > 0 ? Math.round((passedFirstTime / enteredTesting) * 100) : 0;
  devResults.push({
    developerName: dev, developerAccountId: devMap.get(dev) || "",
    value: pct, label: pct + "%",
    severity: pct >= 80 ? "good" : pct >= 60 ? "warning" : "critical",
    details: passedFirstTime + "/" + enteredTesting + " passed testing first time",
  });
}
const overall = devResults.length > 0 ? Math.round(devResults.reduce((s, r) => s + r.value, 0) / devResults.length) : 0;
return {
  id: 11, name: "First-pass rate",
  description: "Percentage of cards that passed testing without any rework",
  results: devResults, overallValue: overall, overallLabel: overall + "%",
  severity: overall >= 80 ? "good" : overall >= 60 ? "warning" : "critical",
};`,
  },
  {
    id: 12,
    name: "Issues touched",
    description: "Unique issues each developer had activity on (assigned or changelog)",
    type: "lead",
    needsDevInfo: false,
    scope: "per-user",
    source: "jira",
    code: `const devResults = [];
for (const dev of targetDevs) {
  const touched = new Set();
  for (const issue of issues) {
    const histories = issue.changelog?.histories || [];
    for (const h of histories) {
      if (h.author?.displayName?.toLowerCase() === dev) { touched.add(issue.key); break; }
    }
    if (issue.fields.assignee?.displayName.toLowerCase() === dev) touched.add(issue.key);
  }
  devResults.push({
    developerName: dev, developerAccountId: devMap.get(dev) || "",
    value: touched.size, label: "" + touched.size,
    severity: touched.size >= 20 ? "good" : touched.size >= 8 ? "warning" : "critical",
    details: touched.size + " unique issues with activity",
  });
}
const overall = devResults.reduce((s, r) => s + r.value, 0);
return {
  id: 12, name: "Issues touched",
  description: "Unique issues each developer had activity on (assigned or changelog)",
  results: devResults, overallValue: overall, overallLabel: "" + overall,
  severity: overall >= 50 ? "good" : overall >= 20 ? "warning" : "critical",
};`,
  },
  {
    id: 13,
    name: "Fast cycle rate",
    description: "Percentage of completed issues with cycle time ≤5 days (In Progress → Done)",
    type: "lead",
    needsDevInfo: false,
    scope: "per-user",
    source: "jira",
    code: `const devResults = [];
for (const dev of targetDevs) {
  const devIssues = issues.filter(
    (i) => i.fields.assignee?.displayName.toLowerCase() === dev && helpers.isDoneStatus(i.fields.status.name)
  );
  let fastCount = 0;
  let measurable = 0;
  for (const issue of devIssues) {
    const histories = issue.changelog?.histories || [];
    let startDate = null;
    let endDate = null;
    for (const h of histories) {
      for (const item of h.items) {
        if (item.field === "status") {
          if (helpers.isInProgressStatus(item.toString || "") && !startDate) startDate = new Date(h.created);
          if (helpers.isDoneStatus(item.toString || "")) endDate = new Date(h.created);
        }
      }
    }
    if (startDate && endDate) {
      measurable++;
      const days = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
      if (days <= 5) fastCount++;
    }
  }
  const pct = measurable > 0 ? Math.round((fastCount / measurable) * 100) : 0;
  devResults.push({
    developerName: dev, developerAccountId: devMap.get(dev) || "",
    value: pct, label: pct + "%",
    severity: pct >= 60 ? "good" : pct >= 30 ? "warning" : "critical",
    details: fastCount + "/" + measurable + " completed in ≤5 days",
  });
}
const overall = devResults.length > 0 ? Math.round(devResults.reduce((s, r) => s + r.value, 0) / devResults.length) : 0;
return {
  id: 13, name: "Fast cycle rate",
  description: "Percentage of completed issues with cycle time ≤5 days (In Progress → Done)",
  results: devResults, overallValue: overall, overallLabel: overall + "%",
  severity: overall >= 60 ? "good" : overall >= 30 ? "warning" : "critical",
};`,
  },
  {
    id: 14,
    name: "Story points delivered",
    description: "Total story points completed per developer",
    type: "lead",
    needsDevInfo: false,
    scope: "per-user",
    source: "jira",
    code: `const devResults = [];
for (const dev of targetDevs) {
  const devIssues = issues.filter(
    (i) => i.fields.assignee?.displayName.toLowerCase() === dev && helpers.isDoneStatus(i.fields.status.name)
  );
  let totalPoints = 0;
  for (const issue of devIssues) {
    const sp = issue.fields.story_points || issue.fields.customfield_10016 || 0;
    totalPoints += sp;
  }
  devResults.push({
    developerName: dev, developerAccountId: devMap.get(dev) || "",
    value: totalPoints, label: "" + totalPoints,
    severity: totalPoints >= 20 ? "good" : totalPoints >= 8 ? "warning" : "critical",
    details: totalPoints + " story points delivered",
  });
}
const overall = devResults.reduce((s, r) => s + r.value, 0);
return {
  id: 14, name: "Story points delivered",
  description: "Total story points completed per developer",
  results: devResults, overallValue: overall, overallLabel: "" + overall,
  severity: overall >= 50 ? "good" : overall >= 20 ? "warning" : "critical",
};`,
  },
  {
    id: 15,
    name: "Estimation accuracy",
    description: "Percentage of issues completed within 150% of original time estimate",
    type: "lead",
    needsDevInfo: false,
    scope: "per-user",
    source: "jira",
    code: `const devResults = [];
for (const dev of targetDevs) {
  const devIssues = issues.filter(
    (i) => i.fields.assignee?.displayName.toLowerCase() === dev && helpers.isDoneStatus(i.fields.status.name)
  );
  let estimated = 0;
  let accurate = 0;
  for (const issue of devIssues) {
    const est = issue.fields.timeoriginalestimate;
    const spent = issue.fields.timespent;
    if (est && est > 0 && spent) {
      estimated++;
      if (spent <= est * 1.5) accurate++;
    }
  }
  const pct = estimated > 0 ? Math.round((accurate / estimated) * 100) : 0;
  devResults.push({
    developerName: dev, developerAccountId: devMap.get(dev) || "",
    value: pct, label: estimated > 0 ? pct + "%" : "N/A",
    severity: estimated === 0 ? "warning" : pct >= 70 ? "good" : pct >= 40 ? "warning" : "critical",
    details: accurate + "/" + estimated + " issues completed within 150% of estimate",
  });
}
const overall = devResults.length > 0 ? Math.round(devResults.filter(r => r.label !== "N/A").reduce((s, r) => s + r.value, 0) / Math.max(devResults.filter(r => r.label !== "N/A").length, 1)) : 0;
return {
  id: 15, name: "Estimation accuracy",
  description: "Percentage of issues completed within 150% of original time estimate",
  results: devResults, overallValue: overall, overallLabel: overall + "%",
  severity: overall >= 70 ? "good" : overall >= 40 ? "warning" : "critical",
};`,
  },
  {
    id: 16,
    name: "Bugs resolved",
    description: "Number of bug-type issues resolved per developer",
    type: "lead",
    needsDevInfo: false,
    scope: "per-user",
    source: "jira",
    code: `const devResults = [];
for (const dev of targetDevs) {
  const bugsFixed = issues.filter(
    (i) =>
      i.fields.assignee?.displayName.toLowerCase() === dev &&
      i.fields.issuetype.name.toLowerCase() === "bug" &&
      helpers.isDoneStatus(i.fields.status.name)
  );
  devResults.push({
    developerName: dev, developerAccountId: devMap.get(dev) || "",
    value: bugsFixed.length, label: "" + bugsFixed.length,
    severity: bugsFixed.length >= 5 ? "good" : bugsFixed.length >= 2 ? "warning" : "critical",
    details: bugsFixed.length + " bugs resolved",
    issueKeys: bugsFixed.map(i => i.key),
  });
}
const overall = devResults.reduce((s, r) => s + r.value, 0);
return {
  id: 16, name: "Bugs resolved",
  description: "Number of bug-type issues resolved per developer — shows responsiveness",
  results: devResults, overallValue: overall, overallLabel: "" + overall,
  severity: overall >= 10 ? "good" : overall >= 3 ? "warning" : "critical",
};`,
  },
  {
    id: 17,
    name: "Component breadth",
    description: "Unique components each developer worked on — indicates cross-area knowledge",
    type: "lead",
    needsDevInfo: false,
    scope: "per-user",
    source: "jira",
    code: `const devResults = [];
for (const dev of targetDevs) {
  const devIssues = issues.filter(
    (i) => i.fields.assignee?.displayName.toLowerCase() === dev
  );
  const components = new Set();
  for (const issue of devIssues) {
    for (const comp of issue.fields.components || []) {
      components.add(comp.name);
    }
  }
  devResults.push({
    developerName: dev, developerAccountId: devMap.get(dev) || "",
    value: components.size, label: "" + components.size,
    severity: components.size >= 3 ? "good" : components.size >= 2 ? "warning" : "critical",
    details: components.size > 0 ? [...components].join(", ") : "No components tagged",
  });
}
const overall = devResults.length > 0 ? Math.round(devResults.reduce((s, r) => s + r.value, 0) / devResults.length) : 0;
return {
  id: 17, name: "Component breadth",
  description: "Unique components each developer worked on — indicates cross-area knowledge",
  results: devResults, overallValue: overall, overallLabel: overall + " avg",
  severity: overall >= 3 ? "good" : overall >= 2 ? "warning" : "critical",
};`,
  },
  {
    id: 18,
    name: "Consistent delivery",
    description: "Number of distinct weeks where developer completed at least one issue",
    type: "lead",
    needsDevInfo: false,
    scope: "per-user",
    source: "jira",
    code: `const devResults = [];
for (const dev of targetDevs) {
  const devDone = issues.filter(
    (i) => i.fields.assignee?.displayName.toLowerCase() === dev && helpers.isDoneStatus(i.fields.status.name)
  );
  const weeks = new Set();
  for (const issue of devDone) {
    const resolved = issue.fields.resolutiondate || issue.fields.updated;
    if (resolved) {
      const d = new Date(resolved);
      const yr = d.getFullYear();
      const wk = Math.ceil(((d.getTime() - new Date(yr, 0, 1).getTime()) / 86400000 + new Date(yr, 0, 1).getDay() + 1) / 7);
      weeks.add(yr + "-W" + wk);
    }
  }
  devResults.push({
    developerName: dev, developerAccountId: devMap.get(dev) || "",
    value: weeks.size, label: weeks.size + " weeks",
    severity: weeks.size >= 8 ? "good" : weeks.size >= 4 ? "warning" : "critical",
    details: "Delivered in " + weeks.size + " distinct weeks",
  });
}
const overall = devResults.length > 0 ? Math.round(devResults.reduce((s, r) => s + r.value, 0) / devResults.length) : 0;
return {
  id: 18, name: "Consistent delivery",
  description: "Number of distinct weeks where developer completed at least one issue",
  results: devResults, overallValue: overall, overallLabel: overall + " wk avg",
  severity: overall >= 8 ? "good" : overall >= 4 ? "warning" : "critical",
};`,
  },
];
